import React, { useEffect, useState } from "react";
import styled from "styled-components";
import { DeleteGrayIcon } from "../Icon/DeleteGrayIcon";

import { IDisplayModeValueEditor, IEditModeValueEditor } from "./Common";

import { DeleteButton, Row } from "./DesignUI";
import { StyledLink } from "../BuildlingBlocks/StyledLink";
import { ListValueRender } from "./DisplayModeRender";
import { UISwitch } from "../BuildlingBlocks/UISwitch";
import { UIStyleConstants } from "../../rule-engine-core/Common/UIStyleConstants";
import type { AppGlueAny } from "../../rule-engine-core/Common/AppGlueAny";

export const DisplayModeBooleanValueEditor = ({ value, forData }: IDisplayModeValueEditor) => {
    let label: JSX.Element | null = null;

    return (
        <div
            className="ValueEditor-display"
            data-testid="ValueEditor-Boolean-Display-switch"
        >
            {label}
            {Array.isArray(value) ? (
                <ListValueRender values={value} />
            ) : (
                <UISwitch
                    value={value}
                    data-testid={`${forData.name}-value-switch`}
                    onChange={() => {}}
                />
            )}
        </div>
    );
};

export const EditModeBooleanValueEditor = React.forwardRef<HTMLElement, IEditModeValueEditor>(
    ({ value, forData, onUpdate }: IEditModeValueEditor, _ref) => {
        let values = forData.list
            ? Array.isArray(value) && typeof value !== "string"
                ? value
                : [value]
            : Array.isArray(value) && typeof value !== "string"
              ? value[0]
              : value;

        const onValueUpdate = (newValues: boolean[]) => {
            onUpdate(newValues);
        };

        if (forData.list) {
            return (
                <EditorBooleanList
                    values={values}
                    updateCallback={onValueUpdate}
                />
            );
        }

        return (
            <div
                className="ValueEditor-display"
                data-testid="ValueEditor-Boolean-Edit-switch"
            >
                <UISwitch
                    value={values}
                    onChange={(newValue) => {
                        onUpdate(newValue);
                    }}
                    data-testid={`${forData.name}-value-switch`}
                />
            </div>
        );
    },
);

export interface IEditorBooleanList {
    values: AppGlueAny[];
    updateCallback: (vals: boolean[]) => void;
}

export const EditorBooleanList: React.FC<IEditorBooleanList> = (props: IEditorBooleanList) => {
    const [values, setValues] = useState<AppGlueAny[]>(props.values);

    useEffect(() => {
        setValues([...props.values]);
    }, [props.values, setValues]);

    const update = (vals: boolean[]) => {
        props.updateCallback(vals);
        setValues([...vals]);
    };

    const onChange = (newValue: boolean, selectedLine: number) => {
        if (selectedLine >= 0) {
            values[selectedLine] = newValue;
            update(values);
        }
    };

    const add = () => {
        values.push(false);
        update(values);
    };

    const remove = (index: number) => {
        values.splice(index, 1);
        update(values);
    };

    return (
        <StyledBooleanList
            className="PropertyList-Wrapper BooleanList-Wrapper"
            data-testid="ValueEditor-BooleanList-Edit-switch"
        >
            <div className="BooleanList">
                {values &&
                    values.map((val: boolean, idx: number) => (
                        <Row key={idx}>
                            <UISwitch
                                value={val}
                                key={idx}
                                onChange={(newValue) => onChange(newValue, idx)}
                            />
                            <DeleteButton onClick={() => remove(idx)}>
                                <DeleteGrayIcon />
                            </DeleteButton>
                        </Row>
                    ))}
            </div>
            <StyledLink
                onClick={add}
                sx={{ background: UIStyleConstants.BLUE_COLOR }}
                classes={{ root: "PropertyList-Add" }}
            >
                Add
            </StyledLink>
        </StyledBooleanList>
    );
};

const StyledBooleanList = styled.div`
    width: 100%;

    text-align: right;

    .BooleanList {
        display: flex;
        flex-direction: column;

        > div {
            margin-top: 5px;

            &:first-child {
                margin-top: 0;
            }
        }
    }

    .BooleanList-Label {
        color: ${UIStyleConstants.TEXT_COLOR_DEFAULT};

        font-style: normal;
        font-weight: 600;
        margin-bottom: 5px;
    }

    .BooleanList-header {
        display: flex;
        justify-content: space-between;
        align-items: center;
    }
`;
